import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import { useEffect, useState } from 'react';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import {
  useInvalidateTargetPrevPurchaseOrders,
  useTargetPrevPurchaseOrders,
} from '@/hooks/api/0574_target_prev_purchase_orders';
import { useInvalidatePrevPurchaseOrders } from '@/hooks/api/0571_prev_purchase_orders';
import { useInvalidateNextPurchaseOrders } from '@/hooks/api/0572_next_purchase_orders';
import { useCreatePrevPurchaseOrder } from '@/hooks/api/2660_create_prev_purchase_orders';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { PurchaseOrderOutlineLogic } from '@/hooks/api/models/logics';
import { SingleValue } from 'react-select';
import { toastApiError } from '@/lib/toast';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { SelectSingle } from '@/components/element/Form/SelectSingle';

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  prev_purchase_order_id: number;
};
export const PrevPurchaseOrderCreateForm = () => {
  const { setShowLoading } = useLoading();
  const { _po_ } = useCurrentPurchaseOrder();
  const { _w_, _t_ } = useCurrentTeamMember();
  const orderParams = { identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id };

  const { data: targetPrevOrders } = useTargetPrevPurchaseOrders(orderParams);
  const { invalidate: invalidatePrevOrders } = useInvalidatePrevPurchaseOrders(orderParams);
  const { invalidate: invalidateNextOrders } = useInvalidateNextPurchaseOrders(orderParams);
  const { invalidate: invalidateTargetPrevOrders } = useInvalidateTargetPrevPurchaseOrders(orderParams);

  const { mutate, data, isPending, error } = useCreatePrevPurchaseOrder();
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  const {
    control,
    getValues,
    setValue,
    trigger,
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
    },
  });

  useEffect(() => {
    if (targetPrevOrders && targetPrevOrders.length > 0) {
      setOptions(
        targetPrevOrders.map((d): SelectOptionType => ({ value: d.id, label: PurchaseOrderOutlineLogic.toLabel(d) }))
      );
    } else {
      setOptions([]);
    }
  }, [targetPrevOrders]);

  const onPurchaseOrderChange = (sv: SingleValue<SelectOptionType>) => {
    if (sv) {
      setValue('prev_purchase_order_id', sv.value);
      trigger('prev_purchase_order_id');
    } else {
      reset(undefined, { keepDefaultValues: true });
    }
  };

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  useEffect(() => {
    if (data) {
      invalidateTargetPrevOrders();
      invalidatePrevOrders();
      invalidateNextOrders();
      reset(undefined, { keepDefaultValues: true });
    }
  }, [data]);

  return (
    <>
      <div className="form-group">
        <label>プロジェクト名</label>
        <p className="form-text text-muted text-sm ms-1">{_po_!.name}</p>
      </div>
      <hr className="horizontal dark my-3" />
      <div className="col-12 col-md-9">
        <FormGroup label="以前のプロジェクト（費用）を選択" required={true} classes={''}>
          <Controller
            control={control}
            name="prev_purchase_order_id"
            rules={{ required: true }}
            render={({ field: { ref, ...field } }) => (
              <SelectSingle
                options={options}
                selectedId={getValues('prev_purchase_order_id')}
                onChange={onPurchaseOrderChange}
                error={errors.prev_purchase_order_id != null}
              />
            )}
          />
        </FormGroup>
      </div>
      <div className="col-12 col-md-3">
        <div className="form-group">
          <label>&nbsp;</label>
          <button className="btn w-100 btn-icon bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
            <i className="fa fa-plus"></i>追加
          </button>
        </div>
      </div>
    </>
  );
};
